import UTMPurser from "../purser/index";
import axios from "axios";
import getCountryCode from "./getCountryCode";
import uniqid from "uniqid";
import dayjs from "dayjs";
import { normalizePath } from "@qlik/utils/normalizePathOrUrl";

const hiddenClass = "js-hidden";

/**
 * This function looks for the checkbox text in the form from the qlik marketo instance
 * and then copies it and puts it where the checkbox text should be in our talend/stitch marketo instance.
 * This allows us to re-use the existing CSS written for out talend/stitch forms to style it properly with
 * very little effort.
 */
export const fetchCheckboxText = () => {
    // iterate through checkboxes in the form (there should really only be 1 for our forms...)
    let checkWrappers = document.getElementsByClassName("mktoCheckboxList");
    for (let i = 0; i < checkWrappers.length; i++) {
        const checkEl = checkWrappers[i];

        // grab the checkbox text node
        let labelTextNode = checkEl.parentElement.parentElement.parentElement.querySelector(
            ".mktoFormCol > .mktoFieldWrap > .mktoHtmlText > span"
        );

        // get a handle on where we want to copy it to (destination)
        let labelEl = checkEl.parentElement.querySelector("label.mktoLabel");

        // this cleans up the checkbox text destination, for when this function is called multiple times
        // usually due to changing the country dropdown
        labelEl.querySelector("span") && labelEl.querySelector("span").remove();

        // if both the text and the destination are present
        if (labelTextNode && labelEl) {
            // clone the text node
            const labelText = labelTextNode.cloneNode(true);

            // append it into the destination, since there is usually already a asterisk element in there.
            labelEl.appendChild(labelText);

            // in case the cloned node appended into 'labelEl' contains a 'hidden' class, remove it
            labelEl.querySelector("span").classList.contains("hidden") &&
                labelEl.querySelector("span").classList.remove("hidden");

            // hide the original text node, it gets overwritten on country update, so can't remove it
            labelTextNode.classList.add("hidden");
        }
    }
};

export const applyCheckboxClasses = () => {
    // Adding class to field wrapper elements when checkbox is present
    let checkWrappers = document.getElementsByClassName("mktoCheckboxList");
    for (let i = 0; i < checkWrappers.length; i++) {
        const checkEl = checkWrappers[i];
        checkEl.parentNode.classList.add("js-has-checkbox");
    }
};

// UTM / Purser value mapping & field setting
export const addPurserValuesToMarketoForms = (form) => {
    // Get the values from the Purser
    const purserValues = UTMPurser.get();

    // Instantiate a variable for use later
    let latestValues;

    // If the visitor has been to the site before, pull the values from the latest visit
    if (Object.prototype.hasOwnProperty.call(purserValues, "visits")) {
        latestValues = purserValues["visits"][`${purserValues["visits"].length - 1}`];
    } else {
        // Otherwise, pull the values from the initial visit
        latestValues = purserValues;
    }

    // Get the current page URL
    const currUrl = window.location.pathname;

    // Split the URL into segments
    const currURLSegments = currUrl.split("/");

    // Define the "landing page" URL segments
    const landingURLSegments = new URL(latestValues.landing_page).pathname.split("/");

    // The first segment should be the language (ie: "us") so if it's empty, remove it from the array
    landingURLSegments.forEach((segment, index) => segment === "" && landingURLSegments.splice(index, 1));

    // The first segment should be the language (ie: "us") so if it's empty, remove it from the array
    currURLSegments.forEach((segment, index) => segment === "" && currURLSegments.splice(index, 1));

    // UTM Medium & UTM Source depend on the referrer
    if (!latestValues.utm_medium && latestValues.referrer) {
        // UTM Medium:
        // If the original referrer value contains contains any of the organic domains, set utm_medium to "organic"
        const organicDomains = ["google.com", "bing.com", "baidu.com", "yandex.com", "yahoo.com"];

        organicDomains.forEach((domain) => {
            // Checking against original purserValues.referrer, not latestValues.referrer
            if (latestValues.referrer.includes(domain)) {
                latestValues.utm_medium = "organic";
            }
        });

        // UTM Source:
        // Strip the protocol, the www, the .com, and strip anything past the first slash, including the first slash
        if (!latestValues.utm_source) {
            latestValues.utm_source = latestValues.referrer
                .replace(/(^\w+:|^)\/\//, "") // Strip the protocol
                .replace("www.", "") // Strip the www
                .replace(".com", "") // Strip the .com
                .replace(/\/.*/, ""); // Strip anything past the first slash, including the first slash
        }
    }

    // If the result is "qlik", then set utm_source to a blank value ("")
    if (latestValues.utm_source === "qlik") latestValues.utm_source = "";

    // If utm_source is "Referral", then set to "Direct"
    if (String(latestValues.utm_source).toLowerCase() === "referral") latestValues.utm_medium = "Direct";

    // UTM Team:
    // if utm_team is not set, default to "WEB"
    if (!latestValues.utm_team) latestValues.utm_team = "WEB";

    // UTM Subtype: Generally captures the "first part" of the URL segments
    // If the current (registration) URL has >= 2 segments (including lang):
    // then get the first two segments & strip off any slashes and dashes.
    // ie: "/us/products/data-integration/another-path-here" --> "us-products"
    if (!latestValues.utm_subtype && landingURLSegments.length >= 2) {
        latestValues.utm_subtype = landingURLSegments
            .slice(0, 2)
            .join("/")
            .replaceAll("-", "")
            .replaceAll("/", "-");
    }

    // UTM Content: Generally captures the "last part" of the URL segments (2nd piece and above)
    // When utm_content is not set via query parameters on the URL:
    //  ... and when there is one segment (not including lang): utm_content should be set to “home”.
    //  ... and when there’s more than one segment (not including lang):  utm_content should be a string of all URL
    // The param should be the 3rd-and-forward segments concatenated with dashes removed, and slashes (“/”) to dashes.
    // ie: "/us/products/data-integration/another-path-here" --> "dataintegration-anotherpathhere"

    if (!latestValues.utm_content) {
        if (landingURLSegments.length <= 2) {
            latestValues.utm_content = "home";
        } else if (landingURLSegments.length > 2) {
            latestValues.utm_content = landingURLSegments
                .slice(2)
                .join("/")
                .replaceAll("-", "")
                .replaceAll("/", "-");
        }
    }

    // Mapping of UTM / Purser values to Marketo field ids
    const mapping = {
        utm_source: "utm_source__c",
        utm_medium: "utm_medium__c",
        utm_campaign: "utm_campaign__c",
        utm_content: "utm_content__c",
        utm_term: "utm_term__c",
        utm_team: "UTM_Team__c",
        utm_subtype: "UTM_SubType__c",
        utm_partner_id: "UTM_Partner_ID__c",
        utm_mpt_id: "UTM_MPT_ID__c",
        ppc_id: "PPC_ID__c",
        gclid: "GCLID__c",
    };

    // Reduce the mapped values to only those that are present in the mapping object, and match with the IDs
    // If the value is not present in the mapping object, set it a string value of "Default"
    const mappedValues = Object.keys(mapping).reduce((newData, key) => {
        if (Object.prototype.hasOwnProperty.call(latestValues, key)) {
            newData[mapping[key]] = latestValues[key];
        } else {
            newData[mapping[key]] = "Default";
        }
        return newData;
    }, {});

    // Set the form fields to corresponding values from mappedValues
    form.setValues(mappedValues);

    // Get the form element
    const formEl = form.getFormElem()[0];

    // Set the form's "URL" value to the current registration form page's URL
    formEl.url__c !== undefined
        ? form.setValues({ url__c: window.location.href })
        : form.addHiddenFields({ url__c: window.location.href });
};

/**
 * Form onSuccess Callbacks
 */

// Will create an account in the Talend Trust Assessor App, and redirect there
export const trustAssessor = (form) => {
    const email = form.vals().Email;
    const ENDPOINT = "https://assessor.trustscore.talend.com/user/register";
    const FRONTEND = "https://assessor.trustscore.talend.com/saveuser/";
    const STORAGE_KEY = "@talend-trust-assessor";

    const onRegisterSuccess = (response) => {
        if (oReq.status === 200) {
            localStorage.setItem(STORAGE_KEY, JSON.parse(oReq.responseText).unique_id);
            window.location.href = FRONTEND + JSON.parse(oReq.responseText).unique_id;
        }
    };

    let oReq = new XMLHttpRequest();
    oReq.onload = onRegisterSuccess;
    oReq.open("POST", ENDPOINT, true);
    oReq.send(JSON.stringify({ email: email, responses: form.vals() }));

    // Return false to prevent the submission handler continuing with its own processing
    return false;
};

// Will redirect the user a Stitch Sign Up form with the email pre-filled.
export const stitchSignup = (form) => {
    const vals = form.vals();
    window.location.href = "https://app.stitchdata.com/signup?email=" + vals.Email;
    return false;
};

// Will direct the user to the successURL or will show a thank you message
export const basicActionAfterSubmission = (form, i18n, thankYouMessage, formConversionType = "none", successUrl) => {
    const formEl = form.getFormElem()[0];

    if (!successUrl) {
        // Add the conversion type to the URL, as well as a "thank-you" string
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set("ty", formConversionType);
        urlParams.set("thank-you", true);

        // Construct the new URL
        var newURL =
            window.location.protocol +
            "//" +
            window.location.host +
            window.location.pathname +
            "?" +
            urlParams.toString();

        // Update the URL without reloading the page
        window.history.pushState({ path: newURL }, "", newURL);

        // Hide the form element
        formEl.classList.add(hiddenClass);
        formEl.style.display = "none";

        // Define the form Container
        const formContainer = document.querySelector(`.formHolder.form-id-${form.getId()}`);

        // Remove all child nodes in the form container
        var child = formContainer.lastElementChild;
        while (child) {
            formContainer.removeChild(child);
            child = formContainer.lastElementChild;
        }

        // Add span tag with class='thank-you-message' with the thank you text
        if (document.querySelectorAll("span.thank-you-message").length === 0) {
            const thankYou = document.createElement("span");
            thankYou.classList.add("thank-you-message");
            thankYou.innerHTML = thankYouMessage;
            formContainer.appendChild(thankYou);
        }
    } else {
        const url = i18n.pathHasLangPrefix(successUrl) ? successUrl : i18n.getLangPrefixedPath(successUrl);

        // Direct the user to the success URL
        window.location.href = window.location.origin + normalizePath(`/${url}/`);
    }

    // Return false to prevent Marketo continuing with its own post-submission behavior
    return false;
};

// Function which submits form data to a corresponding Netlify function
export const createTrial = async (trialFuncName, formValues, form, submitToMarketo, callbackAction) => {
    // Create array to hold all asymc fetch/responses
    const promiseArr = [];

    // Submit the form data to the corresponding Netlify function
    const trialResponse = await fetch(`/.netlify/functions/${trialFuncName}`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(formValues),
    });

    // Add the trial response to the response/Promise array
    promiseArr.push(trialResponse);

    // When kicking off a Replicate Trial, also submit the form data to Marketo
    if (submitToMarketo) {
        const marketoResponse = await marketoSubmit(formValues);

        // Add the marketo response to the response/Promise array
        promiseArr.push(marketoResponse);

        // if marketoResponse is not ok, log the error and show the error message
        if (!marketoResponse?.ok) {
            console.error(marketoResponse.statusText);
            form.showErrorMessage(form.form_submission_error);
        }
    }

    // Wait for all responses to resolve
    const allResponses = await Promise.all(promiseArr);

    // Upon success, fire the customCallback
    if (allResponses.every((response) => response.ok) && callbackAction) {
        callbackAction();
    } else {
        console.error(trialResponse.statusText);
        form.showErrorMessage(form.form_submission_error);
    }
};

// Function which submits form data to the marketoSubmit netlify function,
// which uses the Marketo REST API to submit the form data
const marketoSubmit = async (formValues) => {
    return await fetch("/.netlify/functions/marketoSubmit", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(formValues),
    }).catch((err) => {
        console.error(err);
    });
};

// Custom submission func which passes data to a Netlify function which then sends to Qlik's custom Azure instance
export const formAzureSubmit = (
    form,
    i18n,
    thankYouMessage,
    formConversionType,
    azureFuncName,
    successUrl = false,
    programName,
    formCallbackType
) => {
    // If the user has opted-in to the email list, set unsubscribed to false
    const unsubbedVal = form.vals().emailIn === "Yes" ? false : true;

    // Define whether it's the prs-queue or not
    const isPRSQ = azureFuncName === "prs-queue" ? true : false;

    // Define the base JSON body
    const jsonBody = {
        firstName: form.vals().FirstName,
        lastName: form.vals().LastName,
        email: form.vals().Email,
        city: "",
        state: form.vals().State ?? "",
        company: form.vals().Company,
        country: form.vals().Country,
        countryCode: getCountryCode(form.vals().Country),
        url__c: UTMPurser.get().landing_page ?? "",
        comments__c: form.vals().comments__c ?? "",
        utm_source__c: form.vals().utm_source__c ?? "",
        utm_medium__c: form.vals().utm_medium__c ?? "",
        utm_campaign__c: form.vals().utm_campaign__c ?? "",
        utm_team__c: form.vals().UTM_Team__c ?? "",
        utm_term__c: form.vals().utm_term__c ?? "",
        utm_content__c: form.vals().utm_content__c ?? "",
        utm_subtype__c: form.vals().UTM_SubType__c ?? "",
        ppc_id__c: form.vals().PPC_ID__c ?? "",
        utm_mpt_id__c: form.vals().UTM_MPT_ID__c ?? "",
        utm_partner_id__c: form.vals().UTM_Partner_ID__c ?? "",
        gclid: form.vals().GCLID__c ?? "",
    };

    // Add prs-queue-specific body values: The PRS Queue is used to initiate Qlik Sense Business trials
    if (isPRSQ) {
        jsonBody.telephone = form.vals().Phone; // Field name differs from marketo-queue
        jsonBody.jobTitle = form.vals().Title; // Field name differs from marketo-queue
        jsonBody.callbackUrl = process.env.GATSBY_AZURE_PRS_QUEUE_CALLBACK_URL;
        jsonBody.campaignID = "7013z000000isbU"; // QSB's Salesforce campaign ID
        jsonBody.contentType = "application/json";
        jsonBody.eventID = uniqid("web-");
        jsonBody.eventTime = dayjs().format("MM/DD/YYYY hh:mm:ss A"); // needs to be "12/11/2023 12:29:00 PM" format
        jsonBody.eventType = "web.leadgeneration";
        jsonBody.formType = "qlik-sense-business";
        jsonBody.sku = process.env.GATSBY_AZURE_PRS_QUEUE_SKU; // Determined from legacy SiteCore form
        jsonBody.source = "qlik.com";
        jsonBody.marketoAPIProgramName = programName ?? "19Q2_TRI_DA_GBL_QlikSenseBusinessTrial_2663"; // From SiteCore QSB form
        jsonBody.marketoAPIReason = programName ?? "19Q2_TRI_DA_GBL_QlikSenseBusinessTrial_2663";
        jsonBody.url = window.location.href; // Required by PRS queue. Not used by Marketo.
        jsonBody.incentive = "Free Trial";
        jsonBody.privacyPolicyAccepted = "True";
        jsonBody.campaignReferrer = "";
        // The UTM values in the JSON body for the PRS queue use the basic naming convention, not Marketo's
        jsonBody.utm_content = jsonBody.utm_content__c;
        jsonBody.utm_source = jsonBody.utm_source__c;
        jsonBody.utm_medium = jsonBody.utm_medium__c;
        jsonBody.utm_campaign = jsonBody.utm_campaign__c;
        jsonBody.utm_term = jsonBody.utm_term__c;
        jsonBody.utm_team = jsonBody.utm_team__c;
        jsonBody.utm_subtype = jsonBody.utm_subtype__c;
        jsonBody.ppc_id = jsonBody.ppc_id__c;
        jsonBody.utm_mpt_id = jsonBody.utm_mpt_id__c;
        jsonBody.utm_partner_id = jsonBody.utm_partner_id__c;
    }

    // Piggybacking on Qlik Sense Business set up for temporary (🤞) Talend trial workaround.
    if (formCallbackType === "Azure - Talend Trial") {
        jsonBody.sku = "talend"; // Alter the SKU to let PRS know this is supposed to be a Talend trial.
    }

    // Add marketo-queue-specific body values
    if (!isPRSQ) {
        // programName required by Marketo's Lead API; must match a program in Marketo tho it's not used by MarTech
        // Currently ResourceLibraryWithoutCampaign is used as catch-all program name for all forms
        jsonBody.marketoAPIProgramName = programName ?? "18Q2_GAT_DA_GBL_ResourceLibraryWithoutCampaign_1430";
        jsonBody.marketoAPIReason = "";
        jsonBody.phone = form.vals().Phone;
        jsonBody.title = form.vals().Title;
        jsonBody.unsubscribed = unsubbedVal;
    }

    // Set up object for passing body and function name to Netlify function
    const data = {
        azureFuncName: azureFuncName,
        jsonBody: jsonBody,
    };

    // Set up object for passing data to Netlify function
    const sendConfig = {
        endpoint: "/.netlify/functions/azureSubmit",
        data, // Data to send, from above
        form, // Form object
        i18n,
        thankYouMessage, // Thank you message, from MarketoForm.js
        formConversionType, // Form conversion type, from MarketoForm.js
        successUrl, // Success URL, from MarketoForm.js; defaults to false
    };

    return sendFormData(...Object.values(sendConfig));
};

// Send form data to a Netlify function, which will pass the data to Qlik's custom Azure instance
const sendFormData = async (endpoint, data, form, i18n, thankYouMessage, formConversionType, successUrl = false) => {
    axios
        .post(endpoint, data)
        .then((response) => {
            // Callback is passed from MarketoForm.js, and is dependent on the form-type
            basicActionAfterSubmission(form, i18n, thankYouMessage, formConversionType, successUrl);
        })
        .catch((err) => {
            // Handle errors, uses translated strings, passed from MarketoForm.js
            form.showErrorMessage(form.form_submission_error);
            // eslint-disable-next-line no-console
            console.error(err);
        });
};
